import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/Input/Input';
import { FaTimes, FaUpload } from 'react-icons/fa';
import './AddCategoryModal.css';
import { BASE_URL } from '../../../utils/configConstants';

const getCategoryMediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.replace(/^\//, '')}`;
};

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 5 * 1024 * 1024;

const CategoryImageUpload = ({
  label,
  preview,
  error,
  inputRef,
  onChange,
  uploadText,
}) => (
  <div className="category-image-upload-section">
    <label className="category-image-upload-label">{label}</label>
    <div className="category-image-upload-container">
      {preview ? (
        <div className="category-image-preview-wrapper" onClick={() => inputRef.current?.click()}>
          <img src={preview} alt={`${label} preview`} className="category-image-preview" />
        </div>
      ) : (
        <div className="category-image-upload-area" onClick={() => inputRef.current?.click()}>
          <FaUpload className="category-image-upload-icon" />
          <p className="category-image-upload-text">{uploadText}</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={onChange}
        style={{ display: 'none' }}
      />
    </div>
    {error && <div className="category-image-error">{error}</div>}
  </div>
);

const AddCategoryModal = ({
  showModal,
  isEditMode,
  categoryName,
  setCategoryName,
  saveLoading,
  onSave,
  onClose,
  selectedCategory,
}) => {
  const [categoryImage, setCategoryImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [categoryThumbnail, setCategoryThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState('');
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  const handleRemoveImage = () => {
    setCategoryImage(null);
    setImagePreview(null);
    setImageError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveThumbnail = () => {
    setCategoryThumbnail(null);
    setThumbnailPreview(null);
    setThumbnailError('');
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleFileChange = (e, { setFile, setPreview, setError, fieldLabel }) => {
    const file = e.target.files[0];
    setError('');

    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please select a valid image file (JPEG or PNG)');
      return;
    }

    if (file.size > MAX_SIZE) {
      setError(`${fieldLabel} size should be less than 5MB`);
      return;
    }

    setFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;

    if (isEditMode && selectedCategory) {
      if (selectedCategory.image) {
        setImagePreview(getCategoryMediaUrl(selectedCategory.image));
        setCategoryImage(null);
        setImageError('');
      } else {
        handleRemoveImage();
      }

      if (selectedCategory.thumbnail) {
        setThumbnailPreview(getCategoryMediaUrl(selectedCategory.thumbnail));
        setCategoryThumbnail(null);
        setThumbnailError('');
      } else {
        handleRemoveThumbnail();
      }
    } else {
      handleRemoveImage();
      handleRemoveThumbnail();
    }
  }, [showModal, isEditMode, selectedCategory]);

  if (!showModal) return null;

  const handleSaveWithImage = () => {
    if (!categoryName.trim()) {
      return;
    }

    if (!isEditMode && !categoryImage) {
      setImageError('Please upload a category image');
      return;
    }

    if (!isEditMode && !categoryThumbnail) {
      setThumbnailError('Please upload a category thumbnail');
      return;
    }

    const formData = new FormData();
    formData.append('name', categoryName.trim());

    if (categoryImage) {
      formData.append('image', categoryImage);
    }

    if (categoryThumbnail) {
      formData.append('thumbnail', categoryThumbnail);
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-form">
          <div className="modal-header-inline">
            <h3 className="modal-title">{isEditMode ? 'Edit Category' : 'Add Category'}</h3>
            <button
              type="button"
              className="modal-close-inline"
              onClick={onClose}
              aria-label="Close"
              disabled={saveLoading}
            >
              <FaTimes />
            </button>
          </div>

          <Input
            title="Category Name"
            type="text"
            placeholder="Enter category name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            maxLength={255}
          />

          <CategoryImageUpload
            label="Category Image"
            preview={imagePreview}
            error={imageError}
            inputRef={fileInputRef}
            uploadText="Upload Image"
            onChange={(e) =>
              handleFileChange(e, {
                setFile: setCategoryImage,
                setPreview: setImagePreview,
                setError: setImageError,
                fieldLabel: 'Image',
              })
            }
          />

          <CategoryImageUpload
            label="Category Thumbnail"
            preview={thumbnailPreview}
            error={thumbnailError}
            inputRef={thumbnailInputRef}
            uploadText="Upload Thumbnail"
            onChange={(e) =>
              handleFileChange(e, {
                setFile: setCategoryThumbnail,
                setPreview: setThumbnailPreview,
                setError: setThumbnailError,
                fieldLabel: 'Thumbnail',
              })
            }
          />

          <div className="form-actions">
            <button
              type="button"
              onClick={handleSaveWithImage}
              disabled={saveLoading}
              className="category-save-btn"
            >
              {saveLoading ? (isEditMode ? 'Updating...' : 'Saving...') : isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
